import { useEffect, useState } from 'react'
import * as api from 'api/entityApi'

interface NoteFormProps {
  entityId: string
  entityType?: 'lead' | 'enquiry'
  onSuccess?: (msg: string) => void
  onCancel?: () => void
}

/**
 * Lead: appends a note (CRMService/AddNotes). Enquiry: the legacy page edited
 * one running note — GetInitalRemarksOnly prefills, UpdateIntialRemarks saves.
 */
export function NoteForm({ entityId, entityType = 'lead', onSuccess }: NoteFormProps) {
  const isEnquiry = entityType === 'enquiry'

  const [noteText, setNoteText] = useState<string>('')
  const [isPrivate, setIsPrivate] = useState<boolean>(false)
  const [initialText, setInitialText] = useState<string>('')

  const [loading, setLoading] = useState<boolean>(isEnquiry)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    if (!isEnquiry) return
    let active = true
    api
      .fetchInitialRemarks(entityId, 'enquiry')
      .then((remarks) => {
        if (!active) return
        setNoteText(remarks)
        setInitialText(remarks)
      })
      .catch(() => {
        if (active) setError('Failed to load the existing note.')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [isEnquiry, entityId])

  const handleReset = () => {
    setNoteText(isEnquiry ? initialText : '')
    setIsPrivate(false)
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!noteText.trim()) {
      setError('Note text is required.')
      return
    }
    setError('')
    setSubmitting(true)
    try {
      if (isEnquiry) {
        await api.updateInitialRemarks(entityId, 'enquiry', noteText)
        setInitialText(noteText)
        onSuccess?.('Note updated.')
      } else {
        const msg = await api.submitNote({ entityId, entityType, noteText, isPrivate })
        onSuccess?.(msg)
        setNoteText('')
      }
    } catch (err) {
      setError((err as Error).message || 'Failed to save note.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="widget-loader">Loading note…</div>

  return (
    <div className="action-form">
      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-group">
        <label className="form-label" htmlFor="nt-text">
          Note Content <span className="req">*</span>
        </label>
        <textarea
          id="nt-text"
          rows={4}
          className="form-control"
          placeholder="Add important note or observation regarding this entity..."
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
        />
      </div>

      {!isEnquiry && (
        <div className="form-group flex-row">
          <label className="checkbox-label" htmlFor="nt-private">
            <input
              id="nt-private"
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
            />
            <span>Mark as Private Note</span>
          </label>
        </div>
      )}

      <div className="action-form-footer">
        <button type="button" className="btn btn-default" onClick={handleReset} disabled={submitting}>
          Reset
        </button>
        <button type="button" className="btn pri-btn" disabled={submitting} onClick={handleSubmit}>
          {submitting ? 'Saving…' : isEnquiry ? 'Update Note' : 'Save Note'}
        </button>
      </div>
    </div>
  )
}
